import React from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Icon,
  Chip,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  TextField
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { ApiService } from '../../../../hooks';
import {
  TaskListItemType,
  TaskListItemTypeTranslations,
  TaskListStatus,
  TaskPlanItemType
} from '../../../../models/common';
import { notifications, extractApiErrorMessage } from '../../../../utils';
import {
  HealthSystemDomainTaskLibraryResultTaskOccurrenceViewModel,
  HealthSystemDALModelTaskLibraryTaskPlanTaskOccurrenceItem,
  HealthSystemDALModelTaskLibraryTaskPlanTaskOccurrenceItemDto,
  HealthSystemDALModelTaskListsTaskListSubtask
} from '../../../../api/HealthSystemApi';

interface IProps {
  opened: boolean;
  taskOccurrence: HealthSystemDomainTaskLibraryResultTaskOccurrenceViewModel | null;
  onClose: (refreshed: boolean) => void;
}

interface ItemState {
  taskScheduleItemId: string;
  status: TaskListStatus;
  type: (typeof TaskPlanItemType)[keyof typeof TaskPlanItemType];
  vaccinationType: number | null;
  name: string | null;
  subtasks: SubtaskState[];
}

interface SubtaskState {
  number: string;
  completed: boolean;
  completedDose: number;
  label: string;
}

const STATUS_OPTIONS: { value: TaskListStatus; labelKey: string; icon: string }[] = [
  { value: TaskListStatus.ToDo, labelKey: 'f-tasks-lists-items-todo', icon: 'alarm' },
  { value: TaskListStatus.InProgress, labelKey: 'f-tasks-lists-items-progress', icon: 'hourglass_empty' },
  { value: TaskListStatus.Done, labelKey: 'f-tasks-lists-items-done', icon: 'check_circle' }
];

const toItemState = (item: HealthSystemDALModelTaskLibraryTaskPlanTaskOccurrenceItem): ItemState => ({
  taskScheduleItemId: item.taskScheduleItemId,
  status: item.status as unknown as TaskListStatus,
  type: item.type as unknown as (typeof TaskPlanItemType)[keyof typeof TaskPlanItemType],
  vaccinationType: item.fromVaccinationRule?.type ?? null,
  name: item.fromTaskLibrary?.name ?? null,
  subtasks:
    item.fromVaccinationRule?.subtasks?.map((st: HealthSystemDALModelTaskListsTaskListSubtask) => ({
      number: st.number ?? '',
      completed: st.completed,
      completedDose: st.dose ?? st.completedDose,
      label: [st.number, st.disease, st.medicalSupply, st.location].filter(Boolean).join(' · ')
    })) ?? []
});

const UpdateTaskOccurrenceModal: React.FC<IProps> = ({ opened, taskOccurrence, onClose }) => {
  const { t } = useTranslation();
  const apiService = new ApiService();

  const [items, setItems] = React.useState<ItemState[]>([]);
  const [isBusy, setIsBusy] = React.useState(false);

  React.useEffect(() => {
    if (opened && taskOccurrence) {
      setItems((taskOccurrence.items ?? []).map(toItemState));
    }
  }, [opened, taskOccurrence]);

  const setItemStatus = (idx: number, status: TaskListStatus) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, status } : it)));
  };

  const selectAllSubtasks = (itemIdx: number) => {
    setItems((prev) =>
      prev.map((it, i) =>
        i === itemIdx ? { ...it, subtasks: it.subtasks.map((st) => ({ ...st, completed: true })) } : it
      )
    );
  };

  const clearAllSubtasks = (itemIdx: number) => {
    setItems((prev) =>
      prev.map((it, i) =>
        i === itemIdx ? { ...it, subtasks: it.subtasks.map((st) => ({ ...st, completed: false })) } : it
      )
    );
  };

  const setSubtaskField = (itemIdx: number, subIdx: number, field: keyof SubtaskState, value: boolean | number) => {
    setItems((prev) =>
      prev.map((it, i) =>
        i === itemIdx
          ? {
              ...it,
              subtasks: it.subtasks.map((st, si) => (si === subIdx ? { ...st, [field]: value } : st))
            }
          : it
      )
    );
  };

  const handleSave = () => {
    if (!taskOccurrence) return;

    const body: HealthSystemDALModelTaskLibraryTaskPlanTaskOccurrenceItemDto[] = items.map((it) => ({
      taskScheduleItemId: it.taskScheduleItemId,
      status: it.status,
      fromVaccinationRule: {
        // Injections and SickAnimals items share the same taskScheduleItemId —
        // the backend needs the vaccination type to update the right item. It is held as a plain number,
        // read back from this same DTO field, hence the cast.
        type: it.vaccinationType as HealthSystemDALModelTaskLibraryTaskPlanTaskOccurrenceItemDto['fromVaccinationRule']['type'],
        subtasks: it.subtasks.map((st) => ({
          number: st.number,
          completed: st.completed,
          completedDose: st.completedDose
        }))
      }
    }));

    setIsBusy(true);
    apiService.api.taskList
      .taskListUpdateTaskOccurrence({
        taskScheduleId: taskOccurrence.taskScheduleId,
        plannedDate: taskOccurrence.plannedDate,
        items: body
      })
      .then(() => {
        notifications.success(t('f-com-saved'));
        onClose(true);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => setIsBusy(false));
  };

  const renderStatusToggle = (itemIdx: number, current: TaskListStatus) => (
    <div className="flex gap-2 flex-wrap mt-1">
      {STATUS_OPTIONS.map((opt) => {
        const isActive = current === opt.value;
        const activeClass =
          opt.value === TaskListStatus.Done
            ? '!bg-green-600 !text-white [&_.MuiChip-icon]:!text-white'
            : opt.value === TaskListStatus.InProgress
              ? '!bg-amber-500 !text-white [&_.MuiChip-icon]:!text-white'
              : '!bg-slate-500 !text-white [&_.MuiChip-icon]:!text-white';
        return (
          <Chip
            key={opt.value}
            icon={<Icon fontSize="small">{opt.icon}</Icon>}
            label={t(opt.labelKey)}
            onClick={() => setItemStatus(itemIdx, opt.value)}
            variant={isActive ? 'default' : 'outlined'}
            className={`[&_.MuiChip-icon]:ml-2 ${isActive ? activeClass : ''}`}
          />
        );
      })}
    </div>
  );

  return (
    <Dialog open={opened} onClose={() => onClose(false)} fullWidth maxWidth="sm">
      <DialogTitle>
        <div className="flex items-center gap-2">
          <Icon color="primary">assignment_turned_in</Icon>
          {t('f-tasks-occurrence-update')}
        </div>
      </DialogTitle>

      <DialogContent dividers>
        {items.length === 0 && (
          <p className="text-gray-500 text-sm text-center py-4">{t('f-tasks-lists-items-empty')}</p>
        )}

        <div className="flex flex-col gap-4">
          {items.map((item, itemIdx) => (
            <div
              key={`${item.taskScheduleItemId}-${item.vaccinationType ?? 'n'}`}
              className="border border-gray-200 rounded-lg p-3"
            >
              {/* Item header */}
              <div className="flex items-center gap-2 mb-2">
                <Icon fontSize="small" color="primary">
                  {item.type === TaskPlanItemType.Vaccination
                    ? item.vaccinationType === TaskListItemType.SickAnimals
                      ? 'coronavirus'
                      : 'vaccines'
                    : 'task_alt'}
                </Icon>
                <span className="font-medium text-sm">
                  {item.name ??
                    t(
                      item.vaccinationType !== null
                        ? TaskListItemTypeTranslations[item.vaccinationType as TaskListItemType]
                        : 'f-tasks-lists-items-vaccination'
                    )}
                </span>
              </div>

              {/* Status toggle */}
              {renderStatusToggle(itemIdx, item.status)}

              {/* Vaccination subtasks */}
              {item.type === TaskPlanItemType.Vaccination && item.subtasks.length > 0 && (
                <div className="mt-3 flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-500 uppercase">{t('f-tasks-subtasks')}</span>
                    <div className="flex gap-1">
                      <Button size="small" color="primary" onClick={() => selectAllSubtasks(itemIdx)}>
                        {t('f-com-select-all')}
                      </Button>
                      <Button size="small" onClick={() => clearAllSubtasks(itemIdx)}>
                        {t('f-com-clear-all')}
                      </Button>
                    </div>
                  </div>
                  {item.subtasks.map((st, subIdx) => (
                    <div key={st.number || subIdx} className="flex items-center gap-3 bg-gray-50 rounded px-2 py-1">
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={st.completed}
                            color="primary"
                            onChange={(e) => setSubtaskField(itemIdx, subIdx, 'completed', e.target.checked)}
                          />
                        }
                        label={<span className="text-sm">{st.label || `#${subIdx + 1}`}</span>}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label="CC"
                        value={st.completedDose}
                        style={{ width: 80 }}
                        inputProps={{ min: 0, step: 0.01 }}
                        onChange={(e) =>
                          setSubtaskField(itemIdx, subIdx, 'completedDose', parseFloat(e.target.value) || 0)
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={isBusy}>
          {t('f-com-cancel')}
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={isBusy}
          startIcon={isBusy ? <CircularProgress size={16} /> : <Icon>save</Icon>}
        >
          {t('f-com-save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateTaskOccurrenceModal;
